import type { LegacyAudienceConfig } from '@/domain/audience/types'

export async function normalizeLegacyStableIds(input: LegacyAudienceConfig): Promise<LegacyAudienceConfig> {
  const config = JSON.parse(JSON.stringify(input)) as LegacyAudienceConfig
  const claimed = new Map<string, string>()
  for (const transport of config.transports?.items || []) {
    transport.id = await stableId('transport', [], transport.id, transport.name || transport.entry?.title || transport.exit?.title || '', claimed)
    const gates = transport.entry?.gates || transport.entryGates || []
    const origins = transport.entry?.origins || []
    const routes = transport.entry?.routes || []
    const destinations = transport.exit?.locations || transport.destinations || []
    transport.entry = { ...(transport.entry || {}), gates, origins, routes }
    transport.exit = { ...(transport.exit || {}), locations: destinations }
    delete transport.entryGates
    delete transport.destinations
    const gateIds = new Map<string, string>()
    const originIds = new Map<string, string>()
    for (const gate of gates) {
      const previousId = gate.id
      gate.id = await stableId('gate', [transport.id], gate.id, gate.name, claimed)
      rememberId(gateIds, previousId, gate.id)
    }
    for (const origin of origins) {
      const previousId = origin.id
      origin.id = await stableId('origin', [transport.id], origin.id, origin.name, claimed)
      rememberId(originIds, previousId, origin.id)
    }
    for (const route of routes) {
      route.originId = referencedId(originIds, route.originId)
      route.gateId = referencedId(gateIds, route.gateId)
      route.id = await stableId('entry-route', [transport.id], route.id, `${route.originId}|${route.gateId}`, claimed)
    }
    for (const destination of destinations) destination.id = await stableId('destination', [transport.id], destination.id, `${destination.name}|${destination.address || ''}`, claimed)
  }
  for (const zone of config.zones?.items || []) zone.id = await stableId('zone', [], zone.id, zone.name, claimed)
  for (const [index, service] of (config.services?.items || []).entries()) {
    service.id = await stableId('service', [], service.id, service.title || service.detailTitle || '', claimed)
    const legacyAnnotationKey = `services.items.${index}.image`
    const stableAnnotationKey = `services.items[${service.id}].image`
    if (config.imageAnnotations?.[legacyAnnotationKey] && !config.imageAnnotations[stableAnnotationKey]) {
      config.imageAnnotations[stableAnnotationKey] = config.imageAnnotations[legacyAnnotationKey]
    }
  }
  return config
}

function rememberId(ids: Map<string, string>, previous: string | undefined, normalized: string): void {
  if (!previous) return
  ids.set(previous, normalized)
  ids.set(previous.trim(), normalized)
}

function referencedId(ids: Map<string, string>, value: string): string {
  return ids.get(value) || ids.get(value.trim()) || value.trim()
}

async function stableId(kind: string, owners: string[], existing: string | undefined, label: string, claimed: Map<string, string>): Promise<string> {
  const normalized = label.trim().replace(/\s+/g, ' ').normalize('NFKC')
  if (!existing?.trim() && !normalized) throw new Error(`${kind} 缺少稳定标识和可生成标识的语义字段`)
  const fingerprint = await digest(JSON.stringify({ kind, owners, label: normalized }))
  const id = existing?.trim() || `${kind}-${fingerprint.slice(0, 16)}`
  const previous = claimed.get(id)
  if (previous && previous !== fingerprint) throw new Error(`稳定标识冲突：${id}`)
  claimed.set(id, fingerprint)
  return id
}

async function digest(value: string): Promise<string> {
  const result = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return [...new Uint8Array(result)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}
